import React, { useEffect, useRef, useState } from 'react';
import { TYPO, FS, SHADCN } from '../theme';
import IconButton from './IconButton';
import { X } from 'lucide-react';

// ── <DetailPanel> ────────────────────────────────────────────────────────────
// Panneau latéral slide-in affichant le détail d'un calcul, style code / doc.
//
// panel = null → caché
// panel = { title, subtitle, rows } → affiché
//
// Types de lignes :
//   { type:'sep',    label }                              → titre de section (## label)
//   { type:'item',   label, value, dim, indent, color, note } → ligne standard
//   { type:'result', label, value, color }                → résultat intermédiaire (= label)
//   { type:'final',  label, value, color }                → résultat final (encadré)
//   { type:'note',   text }                               → commentaire (// text)

const MONO = '"JetBrains Mono", Menlo, "SF Mono", Consolas, monospace';
const PAD_X = 22;

// Style de base — tout le contenu est en monospace
const baseLine = {
  fontFamily: MONO,
  fontSize: 13,
  lineHeight: 1.6,
  fontVariantNumeric: 'tabular-nums',
};

export default function DetailPanel({ panel, onClose }) {
  const lastPanel = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (panel) {
      lastPanel.current = panel;
      const raf = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
  }, [panel]);

  useEffect(() => {
    if (!shown) return;
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [shown, onClose]);

  const current = panel || lastPanel.current;
  if (!current && !shown) return null;

  const isMobile = window.innerWidth < 768;
  const panelW = isMobile ? '100%' : 440;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, pointerEvents: shown ? 'all' : 'none' }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0,
          background: shown ? SHADCN.overlayLight : 'transparent',
          transition: `background ${SHADCN.durSlow} ${SHADCN.easeOut}`,
          pointerEvents: shown ? 'all' : 'none',
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'absolute',
        right: 0, top: 0, bottom: 0,
        width: panelW,
        background: SHADCN.bg,
        boxShadow: SHADCN.shadowPanel,
        display: 'flex',
        flexDirection: 'column',
        transform: shown ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.28s cubic-bezier(0.23, 1, 0.32, 1)',
        overflowY: 'auto',
        // Respect de la safe-area iOS (notch / Dynamic Island) en plein écran mobile
        paddingTop: isMobile ? 'env(safe-area-inset-top)' : 0,
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0,
      }}>
        {/* Header — reste en Inter, c'est un titre humain pas du code */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '20px 22px 16px',
          borderBottom: `1px solid ${SHADCN.border}`,
          position: 'sticky', top: 0, background: SHADCN.bg, zIndex: 1,
          gap: 12,
        }}>
          <div>
            {current.subtitle && (
              <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg, marginBottom: 6 }}>{current.subtitle}</div>
            )}
            <div style={{ ...TYPO.h2, color: SHADCN.fg, fontSize: 17 }}>{current.title}</div>
          </div>
          <IconButton aria-label="Fermer" onClick={onClose}>
            <X size={18} color={SHADCN.mutedFg} strokeWidth={2} />
          </IconButton>
        </div>

        {/* Body — entièrement en mono, look "fichier source / journal compta" */}
        <div style={{ padding: '12px 0 24px', flex: 1, background: SHADCN.bg }}>
          {current.rows.map((row, i) => {

            // ── Section header → `## label` (mono caption, ink3, marginTop) ──
            if (row.type === 'sep') {
              return (
                <div key={i} style={{
                  ...baseLine,
                  color: SHADCN.mutedFg,
                  fontSize: 11,
                  padding: `${i > 0 ? 18 : 6}px ${PAD_X}px 4px`,
                  letterSpacing: 0,
                  textTransform: 'none',
                }}>
                  ## {String(row.label || '').toLowerCase().replace(/\s+/g, '-')}
                </div>
              );
            }

            // ── Note → `// text` (italic, muted) ──────────────────────────
            if (row.type === 'note') {
              return (
                <div key={i} style={{
                  ...baseLine,
                  color: SHADCN.mutedFg,
                  fontSize: 11,
                  fontStyle: 'italic',
                  padding: `8px ${PAD_X}px 4px`,
                }}>
                  // {row.text}
                </div>
              );
            }

            // ── Final → bloc encadré : "RÉSULTAT = +X €" ─────────────────
            if (row.type === 'final') {
              const col = row.color || SHADCN.fg;
              return (
                <div key={i} style={{
                  margin: `16px ${PAD_X}px 4px`,
                  padding: '14px 14px',
                  border: `2px solid ${col}`,
                  background: col,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                  gap: 12,
                }}>
                  <span style={{
                    ...baseLine,
                    fontSize: 11,
                    fontWeight: 700,
                    color: col === SHADCN.error ? SHADCN.bg : SHADCN.fg,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}>
                    {row.label}
                  </span>
                  <span style={{
                    ...baseLine,
                    fontSize: FS.xl,
                    fontWeight: 700,
                    color: col === SHADCN.error ? SHADCN.bg : SHADCN.fg,
                  }}>
                    {row.value}
                  </span>
                </div>
              );
            }

            // ── Result → "= label .... value" (encadré dashed) ───────────
            if (row.type === 'result') {
              const col = row.color || SHADCN.fg;
              return (
                <div key={i} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: `12px ${PAD_X}px`,
                  marginTop: 6,
                  borderTop: `1px dashed ${SHADCN.border}`,
                  borderBottom: `1px dashed ${SHADCN.border}`,
                  gap: 12,
                }}>
                  <span style={{ ...baseLine, fontWeight: 700, color: SHADCN.fg }}>
                    = {row.label}
                  </span>
                  <span style={{ ...baseLine, fontWeight: 700, color: col, fontSize: FS.sm }}>
                    {row.value}
                  </span>
                </div>
              );
            }

            // ── Item (défaut) → label .... value, avec note optionnelle ──
            const col = row.color || (row.dim ? SHADCN.mutedFg : SHADCN.fg);
            return (
              <div key={i} style={{
                padding: `4px ${PAD_X}px`,
                paddingLeft: row.indent ? PAD_X + 16 : PAD_X,
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                  gap: 12,
                }}>
                  <span style={{
                    ...baseLine,
                    color: row.dim ? SHADCN.mutedFg : SHADCN.mutedFg,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {row.label}
                  </span>
                  <span style={{
                    ...baseLine,
                    color: col,
                    fontWeight: row.bold ? 600 : 400,
                    flexShrink: 0,
                  }}>
                    {row.value}
                  </span>
                </div>
                {row.note && (
                  <div style={{
                    ...baseLine,
                    fontSize: 11,
                    color: SHADCN.mutedFg,
                    marginTop: 1,
                  }}>
                    {row.note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
